import { useShopI18n } from '../../i18n'
import { StorefrontProductTopGrid } from '@emporion/ui'
import type { CatalogProduct, CatalogProductFieldDefinition } from '../types'
import ProductImageZoomModal from './ProductImageZoomModal'
import { ProductBreadcrumbs } from './product-page/ProductBreadcrumbs'
import { ProductDetailsPanel } from './product-page/ProductDetailsPanel'
import ProductExtendedSections from './product-page/ProductExtendedSections'
import { ProductImageGallery } from './product-page/ProductImageGallery'
import { ProductLinkedProductsSection } from './product-page/ProductLinkedProductsSection'
import { RecentlyViewedProductsSection } from './product-page/RecentlyViewedProductsSection'
import { useProductShareFeedback } from './product-page/useProductShareFeedback'
import { useProductPageModel } from './product-page/useProductPageModel'
import { getVariantImage, getVariantKey } from './product-page/model/productVariants'

type Props = {
  productData: CatalogProduct | null
  productLoading: boolean
  productError: string
  productGallery: string[]
  activeProductImage: string
  onSelectProductImage: (imageUrl: string) => void
  productFieldDefinitions: CatalogProductFieldDefinition[]
}

const ProductPage = ({
  productData,
  productLoading,
  productError,
  productGallery,
  activeProductImage,
  onSelectProductImage,
  productFieldDefinitions,
}: Props) => {
  const { formatCurrency, toLocalizedPath, locale } = useShopI18n()
  const {
    cartBusy,
    zoomOpen,
    setZoomOpen,
    cartQuantity,
    cartMessage,
    cartMessageTone,
    priceExVat,
    priceIncVat,
    description,
    productVariants,
    selectedVariantKey,
    setSelectedVariantKey,
    categoryBreadcrumbs,
    summaryCustomRows,
    highlightsCustomRows,
    sidebarCustomRows,
    specsSections,
    detailSections,
    linkedProducts,
    linkedProductsLoading,
    recentlyViewedProducts,
    purchasability,
    zoomGallery,
    handleIncreaseCartQuantity,
    handleDecreaseCartQuantity,
    handleAddToCart,
  } = useProductPageModel({
    productData,
    productGallery,
    activeProductImage,
    productFieldDefinitions,
  })
  const { shareFeedback, handleShareProduct } = useProductShareFeedback({ productData, toLocalizedPath })

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: 'var(--theme-color-background, #ffffff)',
        color: 'var(--theme-color-text, #0f172a)',
      }}
    >
      <div className="w-full max-w-none space-y-6 px-4 pb-8 pt-8 sm:px-6 lg:px-10 lg:pb-10 2xl:px-14">
        {productLoading && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
            Caricamento prodotto...
          </div>
        )}
        {productError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
            {productError}
          </div>
        )}
        {shareFeedback ? (
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-800 shadow-sm">
            {shareFeedback}
          </div>
        ) : null}
        {!productLoading && !productError && productData && (
          <div className="space-y-10">
            <ProductBreadcrumbs
              productName={productData.name}
              breadcrumbs={categoryBreadcrumbs}
              toLocalizedPath={toLocalizedPath}
            />

            <StorefrontProductTopGrid className="2xl:grid-cols-[minmax(0,1.35fr)_minmax(420px,0.65fr)]">
              <ProductImageGallery
                activeProductImage={activeProductImage}
                productGallery={productGallery}
                productName={productData.name}
                onSelectProductImage={onSelectProductImage}
                onOpenZoom={() => setZoomOpen(true)}
              />
              <ProductDetailsPanel
                productData={productData}
                formatCurrency={formatCurrency}
                priceExVat={priceExVat}
                priceIncVat={priceIncVat}
                cartQuantity={cartQuantity}
                addToCartPending={cartBusy}
                addToCartMessage={cartMessage}
                addToCartMessageTone={cartMessageTone}
                isPurchasable={purchasability.isPurchasable}
                purchasabilityReason={purchasability.reason}
                stockPolicy={purchasability.stockPolicy}
                availableStock={purchasability.availableStock}
                onIncreaseCartQuantity={handleIncreaseCartQuantity}
                onDecreaseCartQuantity={handleDecreaseCartQuantity}
                onAddToCart={() => {
                  void handleAddToCart()
                }}
                productVariants={productVariants}
                selectedVariantKey={selectedVariantKey}
                onSelectProductVariant={(variantKey) => {
                  setSelectedVariantKey(variantKey)
                  const variant = productVariants.find((item, index) => {
                    return getVariantKey(item, index) === variantKey
                  })
                  const variantImage = getVariantImage(variant ?? null)
                  if (variantImage) onSelectProductImage(variantImage)
                }}
                onShareProduct={() => {
                  void handleShareProduct()
                }}
              />
            </StorefrontProductTopGrid>

            <ProductExtendedSections
              productData={productData}
              description={description}
              locale={locale}
              summaryCustomRows={summaryCustomRows}
              highlightsCustomRows={highlightsCustomRows}
              sidebarCustomRows={sidebarCustomRows}
              specsSections={specsSections}
              detailSections={detailSections}
            />

            <ProductLinkedProductsSection
              products={linkedProducts}
              loading={linkedProductsLoading}
              toLocalizedPath={toLocalizedPath}
              formatCurrency={formatCurrency}
            />

            <RecentlyViewedProductsSection
              products={recentlyViewedProducts}
              toLocalizedPath={toLocalizedPath}
              formatCurrency={formatCurrency}
            />
          </div>
        )}
      </div>
      {zoomOpen && (
        <ProductImageZoomModal
          open={zoomOpen}
          images={zoomGallery}
          activeImage={activeProductImage}
          imageAlt={productData?.name || 'Immagine prodotto'}
          onSelectImage={onSelectProductImage}
          onClose={() => setZoomOpen(false)}
        />
      )}
    </div>
  )
}

export default ProductPage
